import { AxiosResponse } from 'axios';
import { MATCH_STATUS } from '../constants/study.constant';
import api from '../api';
import { DeviceTypes, GENDER, MODALITIES } from '../constants/common.constant';
import { SORT_ORDER } from '../constants/filter.constant';
import { ORDER_STATUS } from '../constants/order.constant';
import {
  DefaultResponse,
  InstitutionProp,
  Procedure,
  UserResponse,
} from '../interfaces/index';

const ORDER_URL = '/orders/v1';

export interface DeviceStatistic {
  device_id?: string;
  device_name: string;
  status_count: {
    [key: number]: number;
  };
}

export interface ResponseGetDeviceStatistics extends DefaultResponse {
  data: DeviceStatistic[];
}
export const getDeviceStatistics = async (params: ParamsGetOrders = {}) => {
  const response = (await api({
    url: ORDER_URL + '/device-statistics',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetDeviceStatistics>;

  return response.data;
};

export interface ParamsGetDeviceStatisticsAll {
  institution_ids?: string[];
  modalities?: string[];
  scheduled_date_from: string;
  scheduled_date_to: string;
}

export const getDeviceStatisticsAll = async (
  params: ParamsGetDeviceStatisticsAll
) => {
  const response = (await api({
    url: ORDER_URL + '/device-statistics/all',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetDeviceStatistics>;

  return response.data;
};

export interface ParamsGetOrders {
  institution_ids?: string[];
  limit?: number;
  match_status?: MATCH_STATUS;
  offset?: number;
  order_group?: string;
  modalities?: string[];
  patient_id?: string;
  patient_name?: string;
  scheduled_date_from?: string;
  scheduled_date_to?: string;
  sort_field?:
    | ''
    | 'order_group'
    | 'patient_id'
    | 'visit_id'
    | 'patient_name'
    | 'scheduled_date'
    | 'patient_arrived_time';
  sort_order?: SORT_ORDER;
  status?: ORDER_STATUS[];
  study_desc?: string;
  study_id?: string;
  visit_id?: string;
}

export interface Order {
  created_at: string;
  device: {
    active: boolean;
    api_key: string;
    code: string;
    created_at: string;
    description: string;
    hostname: string;
    id: string;
    institution_id: string;
    modality: MODALITIES;
    name: string;
    port: number | null;
    sftp_public_key: string;
    sftp_user: string;
    source_aetitle: string;
    type: DeviceTypes;
    updated_at: string;
  };
  device_id: string;
  diagnostic: string;
  id: string;
  institution: InstitutionProp;
  institution_id: string;
  metadata: null | unknown;
  order_doctor_name: string;
  order_group: string;
  patient_arrived_time: string;
  procedure: Procedure;
  procedure_id: string;
  refer_doctor_name: string;
  scheduled_date: string;
  source: number;
  status: number;
  study: {
    comment: string;
    department: string;
    desc: string;
    device_id: string;
    id: string;
    patient_age: string;
    // ref_pacs_id: string; // Not use on FE
    study_date: string;
    study_instance_uid: string;
  };
  study_id: string;
  updated_at: string;
  user: UserResponse;
  user_id: string;
  visited_patient: {
    address: string;
    birth_date: string;
    email: string;
    name: string;
    patient_id: string;
    phone: string;
    sex: GENDER;
    visit_id: string;
  };
  visited_patient_id: string;
}

export interface ResponseGetOrders extends DefaultResponse {
  data: Order[];
}
export const getOrders = async (
  params: ParamsGetOrders = {
    offset: 0,
    limit: 100,
  }
) => {
  const response = (await api({
    url: ORDER_URL,
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetOrders>;

  return response.data;
};

export interface ParamsGetOrderWorklist {
  filter_id?: string;
  genders?: GENDER[];
  institution_ids?: string[];
  limit?: number;
  match?: boolean;
  modalities?: string[];
  offset?: number;
  order_group?: string;
  patient_id?: string;
  patient_name?: string;
  scheduled_date_from?: string;
  scheduled_date_to?: string;
  sort_field?:
    | ''
    | 'order_group'
    | 'patient_id'
    | 'visit_id'
    | 'patient_name'
    | 'scheduled_date'
    | 'patient_arrived_time';
  sort_order?: SORT_ORDER;
  statuses?: ORDER_STATUS[];
  study_desc?: string;
  study_id?: string;
  visit_id?: string;
}
export interface ResponseGetOrderWorklist extends DefaultResponse {
  data: {
    age: string;
    gender: GENDER;
    id: string;
    institution_id: string;
    match: boolean;
    modality: string;
    order_group: string;
    patient_birth_date: string;
    patient_id: string;
    patient_name: string;
    scheduled_date: string;
    status: ORDER_STATUS;
    study_desc: string;
    study_id: string;
    study_instance_uid: string;
    visit_id: string;
  }[];
}
export const getOrderWorklist = async (params: ParamsGetOrderWorklist = {}) => {
  const response = (await api({
    url: ORDER_URL + '/worklist',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetOrderWorklist>;

  return response.data;
};

export interface ResponseGetOrderDetail {
  data: Order;
}
export const getOrderDetail = async (id: string) => {
  const response = (await api({
    url: `${ORDER_URL}/${id}`,
    method: 'GET',
  })) as AxiosResponse<ResponseGetOrderDetail>;

  return response.data;
};

export interface VisitedPatient {
  age: number;
  birth_date: string;
  id: string;
  patient_id: string;
  patient_name: string;
  sex: string;
  visit_id: string;
}
export interface ParamsCreateOrderGroup {
  device_id: string;
  diagnostic?: string;
  institution_id: string;
  order_doctor_name?: string;
  order_group?: string;
  procedure_ids: string[];
  refer_doctor_name?: string;
  scheduled_date: string;
  visited_patient: {
    address?: string;
    birth_date: string;
    email?: string;
    id?: string;
    patient_id?: string;
    patient_name: string;
    phone?: string;
    sex: GENDER;
    visit_id?: string;
  };
}
export const createOrderGroup = async (data: ParamsCreateOrderGroup) => {
  const response = (await api({
    url: ORDER_URL,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsUpdateOrder {
  id: string;
  device_id?: string;
  diagnostic?: string;
  order_doctor_name?: string;
  procedure_id?: string;
  refer_doctor_name?: string;
  scheduled_date?: string;
  status?: number;
  visited_patient: {
    address?: string;
    birth_date?: string;
    email?: string;
    patient_id?: string;
    patient_name?: string;
    phone?: string;
    sex?: number;
  };
}
export const updateOrder = async (data: ParamsUpdateOrder) => {
  const response = (await api({
    url: ORDER_URL + '/' + data.id,
    method: 'PUT',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsDeleteOrder {
  id: string;
}
export const deleteOrder = async (data: ParamsDeleteOrder) => {
  const response = (await api({
    url: ORDER_URL + '/' + data.id,
    method: 'DELETE',
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsMatchOrder {
  order_id: string;
  study_id: string;
}
export const matchOrder = async (data: ParamsMatchOrder) => {
  const response = (await api({
    url: ORDER_URL + '/match',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export const unmatchOrder = async (data: ParamsMatchOrder) => {
  const response = (await api({
    url: ORDER_URL + '/unmatch',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsReceptOrder {
  device_id: string;
  institution_id: string;
  order_ids: string[];
  should_merge_order: boolean;
}
export const receptOrder = async (data: ParamsReceptOrder) => {
  const response = (await api({
    url: ORDER_URL + '/recept',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsChangeOrderDevice {
  device_id: string;
  orders: Order[];
  // should_merge_order: boolean;
}
export const changeOrderDevice = async (data: ParamsChangeOrderDevice) => {
  return await Promise.all(
    data.orders.map((order) =>
      updateOrder({
        ...order,
        visited_patient: order.visited_patient && {
          ...order.visited_patient,
          patient_name: order?.visited_patient?.name,
        },
        device_id: data.device_id,
      })
    )
  );
};
